import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  fetchApiGet,
  fetchApiPost,
  fetchApiPut,
  fetchApiDelete,
} from "./utils";
import { isValidDecimal } from "../excel";
import { CommodityOptions } from "./commodity";

export const otcErrorCorrectionsBaseKey = "otcErrorCorrections";

/**
 * Allowed option-month shape. Matches the backend regex (optionally
 * `O`-prefixed short-dated, e.g. "Z6" or "OZ6").
 */
const OPTION_MONTH_REGEX = /^O?[A-Z][0-9]$/;

const COMMODITY_VALUES: readonly string[] = CommodityOptions.map(
  (option: { value: string }): string => option.value,
);

/**
 * Subset of a user surfaced on error-correction rows for display. The backend
 * returns only the name (a subset of the full user), so model just that.
 */
export interface OtcErrorCorrectionUser {
  name: string | null;
}

/**
 * Response DTO returned by the list and mutation routes. Decimals are
 * serialized to strings and dates to ISO strings by the backend DTO shaper.
 */
export interface OtcErrorCorrectionDto {
  id: string;
  trading_contract_id: string | null;
  trading_contract_layer_id: string | null;
  commodity: string;
  quantity: string;
  option_premium: string;
  option_month: string;
  date: string;
  trader_id: string | null;
  notes: string | null;
  created_by_user_id: string | null;
  updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  CreatedByUser?: OtcErrorCorrectionUser | null;
  UpdatedByUser?: OtcErrorCorrectionUser | null;
}

/**
 * Field-shape validation for creating an error correction. Mirrors the backend
 * `CreateOtcErrorCorrectionValidation`: commodity must be a known commodity
 * value, quantity/option_premium are non-blank numeric strings, option_month
 * matches the option-month regex, date is a `YYYY-MM-DD` portal-date string,
 * trader_id is required. The two contract/layer context links are optional and
 * are not exposed by the standalone form (they are set when creating from a
 * contract's layer).
 */
export const CreateOtcErrorCorrectionValidation = z.object({
  trading_contract_id: z.string().uuid().nullable(),
  trading_contract_layer_id: z.string().uuid().nullable(),
  commodity: z
    .string()
    .min(1, "Commodity is required")
    .refine((value: string): boolean => COMMODITY_VALUES.includes(value), {
      message: "Commodity must be a valid commodity",
    }),
  quantity: z
    .string()
    .min(1, "Quantity is required")
    .refine((value: string): boolean => isValidDecimal(value), {
      message: "Quantity must be a number",
    }),
  option_premium: z
    .string()
    .min(1, "Option premium is required")
    .refine((value: string): boolean => isValidDecimal(value), {
      message: "Option premium must be a number",
    }),
  option_month: z
    .string()
    .min(1, "Option month is required")
    .refine((value: string): boolean => OPTION_MONTH_REGEX.test(value), {
      message: "Option month must be of the format Z6",
    }),
  date: z.string().min(1, "Date is required"),
  trader_id: z.string().uuid({ message: "Trader is required" }),
  notes: z.string().nullable(),
});

export type CreateOtcErrorCorrectionInput = z.infer<
  typeof CreateOtcErrorCorrectionValidation
>;

/**
 * Update/patch schema: only-present keys are applied. Mirrors the backend
 * partial update semantics.
 */
export const UpdateOtcErrorCorrectionValidation =
  CreateOtcErrorCorrectionValidation.partial();

export type UpdateOtcErrorCorrectionInput = z.infer<
  typeof UpdateOtcErrorCorrectionValidation
>;

export function useListOtcErrorCorrections() {
  return useQuery({
    queryKey: [otcErrorCorrectionsBaseKey],
    queryFn: () =>
      fetchApiGet<OtcErrorCorrectionDto[]>("otc/error-corrections"),
    retry: 1,
  });
}

export function useCreateOtcErrorCorrection() {
  const queryClient = useQueryClient();

  return async (input: CreateOtcErrorCorrectionInput) => {
    const result = await fetchApiPost<OtcErrorCorrectionDto>(
      "otc/error-corrections",
      input,
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: [otcErrorCorrectionsBaseKey] });
    }
    return result;
  };
}

export function useUpdateOtcErrorCorrection() {
  const queryClient = useQueryClient();

  return async (id: string, patch: UpdateOtcErrorCorrectionInput) => {
    const result = await fetchApiPut<OtcErrorCorrectionDto>(
      `otc/error-corrections/${id}`,
      patch,
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: [otcErrorCorrectionsBaseKey] });
    }
    return result;
  };
}

export function useDeleteOtcErrorCorrection() {
  const queryClient = useQueryClient();

  return async (id: string) => {
    const result = await fetchApiDelete<OtcErrorCorrectionDto>(
      `otc/error-corrections/${id}`,
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: [otcErrorCorrectionsBaseKey] });
    }
    return result;
  };
}
